import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface GeneratedMatch {
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string;
  awayTeamName: string;
  scheduledDate: string;
  scheduledTime: string;
  fieldId: string;
  fieldName: string;
  round: number;
}

const SeasonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [season, setSeason] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<GeneratedMatch[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchSeason();
  }, [id]);

  const fetchSeason = async () => {
    try {
      const response = await api.get(`/seasons/${id}`);
      setSeason(response.data);
    } catch (err) {
      console.error('Error fetching season:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePreview = async () => {
    setGenerating(true);
    setPreview(null);
    setWarnings([]);
    setSuccessMessage('');
    try {
      const response = await api.post(`/seasons/${id}/generate-schedule`);
      setPreview(response.data.matches || []);
      setWarnings(response.data.warnings || []);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirm = async () => {
    if (!window.confirm(`Create ${preview?.length} matches? This cannot be undone.`)) return;
    setConfirming(true);
    try {
      const response = await api.post(`/seasons/${id}/generate-schedule?confirm=true`);
      setSuccessMessage(`Successfully created ${response.data.matchCount} matches!`);
      setPreview(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create matches');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!season) {
    return (
      <div className="container">
        <h1>Season Not Found</h1>
        <Link to="/seasons" className="btn btn-primary">Back to Seasons</Link>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>{season.name || 'Untitled Season'}</h1>
        <Link to="/seasons" className="btn btn-secondary">Back to Seasons</Link>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <h2>Season Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <p><strong>League:</strong> {season.league?.name || 'N/A'}</p>
          <p><strong>Status:</strong> {season.isDraft ? 'Draft' : 'Finalized'}</p>
          <p><strong>Start Date:</strong> {season.startDate || 'N/A'}</p>
          <p><strong>End Date:</strong> {season.endDate || 'N/A'}</p>
          <p><strong>Teams:</strong> {season.teams?.length || 0}</p>
          <p><strong>Game Days:</strong> {season.gameSchedule?.map((gs: any) =>
            ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][gs.dayOfWeek]
          ).join(', ') || 'None configured'}</p>
        </div>
      </div>

      {season.teams && season.teams.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Teams ({season.teams.length})</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Number</th>
              </tr>
            </thead>
            <tbody>
              {season.teams.map((team: any) => (
                <tr key={team.id}>
                  <td><Link to={`/teams/${team.id}`}>{team.name}</Link></td>
                  <td>{team.teamNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdminOrSuperAdmin(user?.profileType) && !season.isDraft && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Round-Robin Schedule Generator</h2>
          <p style={{ color: '#757575', marginBottom: '15px' }}>
            Generate a complete round-robin schedule where every team plays every other team.
          </p>

          {successMessage && (
            <div style={{ background: '#E8F5E9', padding: '15px', borderRadius: '8px', marginBottom: '15px', color: '#2E7D32' }}>
              {successMessage}
            </div>
          )}

          {!preview && (
            <button
              className="btn btn-primary"
              onClick={handleGeneratePreview}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Round-Robin Schedule'}
            </button>
          )}

          {warnings.length > 0 && (
            <div style={{ background: '#FFF3E0', padding: '15px', borderRadius: '8px', marginTop: '15px' }}>
              <strong>Warnings:</strong>
              <ul style={{ margin: '5px 0 0 0' }}>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {preview && (
            <div style={{ marginTop: '15px' }}>
              <h3>Preview ({preview.length} matches)</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>Round</th>
                    <th>Home</th>
                    <th>Away</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Field</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((m, i) => (
                    <tr key={i}>
                      <td>{m.round}</td>
                      <td>{m.homeTeamName}</td>
                      <td>{m.awayTeamName}</td>
                      <td>{m.scheduledDate}</td>
                      <td>{m.scheduledTime}</td>
                      <td>{m.fieldName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleConfirm}
                  disabled={confirming}
                >
                  {confirming ? 'Creating Matches...' : `Confirm & Create ${preview.length} Matches`}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setPreview(null); setWarnings([]); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SeasonDetail;
